import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaEdit, FaChevronDown } from "react-icons/fa";
import initializeDepartureDate from "../../common/libs/initializeDepartureDate";
import initializeArrivalDate from "../../common/libs/initializeArrivalDate";
import times from "../../common/libs/times";
import { nextPage } from "../../common/config/nextPage";

export default function SearchHeader({ airports, count, handleSort, handleFilter }) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const location = useLocation();
  const pathname = location.pathname;

  const getDeparture = params.get("departure");
  const getArrival = params.get("arrival");
  const getPromocode = params.get("promocode");
  const getAirport = params.get("airport");

  const [isEditing, setIsEditing] = useState(false);
  const [promocode, setPromoCode] = useState(getPromocode || "");
  const [airport, setAirport] = useState(getAirport || "");
  const [selectedAirportLabel, setSelectedAirportLabel] = useState("");

  const [departure, setDeparture] = useState(initializeDepartureDate(getDeparture || ""));
  const [arrival, setArrival] = useState(initializeArrivalDate(getArrival || ""));

  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState("");

  const [activeFilter, setActiveFilter] = useState("all");
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [activeSort, setActiveSort] = useState("");
  const sortRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sortRef.current && !sortRef.current.contains(event.target)) {
        setIsSortOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const onFilterClick = (val) => {
    setActiveFilter(val);
    handleFilter({ target: { value: val } });
  };

  const onSortClick = (val) => {
    setActiveSort(val);
    setIsSortOpen(false);
    handleSort({ target: { value: val } });
  };

  const getSortLabel = () => {
    if (activeSort === "0") return "Price low to high";
    if (activeSort === "1") return "Price high to low";
    return "Select Parking Sort by";
  };

  const getFilterBtnClass = (filterType) => {
    const base = "px-3 py-1.5 md:px-4 md:py-2 rounded-[8px] md:rounded-full transition-all duration-300 font-extrabold tracking-tight text-[11px] md:text-[13px] ";
    return activeFilter === filterType
      ? base + "bg-[#00261E] text-[#EEFE3E] shadow-md"
      : base + "text-[#00261E] hover:bg-[#00261E]/10";
  };

  useEffect(() => {
    if (airport && airports?.length > 0) {
      const found = airports.find((a) => a.code === airport);
      if (found) {
        setSelectedAirportLabel(found.name);
      }
    }
  }, [airport, airports]);

  const setDepartureDate = useCallback((date) => {
    const depTime = moment(departure).format("HH:mm");
    const arrivalTime = moment(arrival).format("HH:mm");
    const depDate = moment(date).format("yyyy-MM-DD");
    const arrivalDate = moment(date).add(7, "days").format("yyyy-MM-DD");

    setDeparture(moment(`${depDate} ${depTime}`).format("yyyy-MM-DD HH:mm"));
    setArrival(moment(`${arrivalDate} ${arrivalTime}`).format("yyyy-MM-DD HH:mm"));
  }, [departure, arrival]);

  const setArrivalDate = useCallback((date) => {
    const arrivalTime = moment(arrival).format("HH:mm");
    const arrivalDate = moment(date).format("yyyy-MM-DD");
    setArrival(moment(`${arrivalDate} ${arrivalTime}`).format("yyyy-MM-DD HH:mm"));
  }, [arrival]);

  const setTime = useCallback((time, mode) => {
    const [h, m] = time.split(":").map((v) => parseInt(v, 10));
    if (mode === "arrival") {
      setArrival((prev) => moment(prev).set("hours", h).set("minutes", m).format("yyyy-MM-DD HH:mm"));
    } else {
      setDeparture((prev) => moment(prev).set("hours", h).set("minutes", m).format("yyyy-MM-DD HH:mm"));
    }
  }, []);

  const handleSubmit = (e) => {
    e?.preventDefault();
    setValidation("");

    if (!airport) {
      setValidation("Please select an airport");
      return;
    }

    const searchParams = new URLSearchParams({
      departure: departure,
      arrival: arrival,
      promocode: promocode,
      airport,
    });

    setLoading(true);
    nextPage(`${pathname}?${searchParams}`);
    setTimeout(() => {
      setLoading(false);
      setIsEditing(false);
    }, 500);
  };

  const inputClass = "w-full bg-[#E4EAE6] rounded-[8px] h-[48px] px-4 font-bold text-gray-800 text-[13px] outline-none focus:ring-2 focus:ring-[#00261E]";
  const labelClass = "text-[13px] font-extrabold text-[#1A1A1A] mb-1.5 ml-0.5 tracking-tight";

  return (
    <div className="w-full bg-white mb-5 px-2">
      {/* Title & Edit Toggle */}
      <div className="flex flex-col items-start mb-4">
        <h1 className="text-[25px] md:text-[40px] font-black text-[#00261E] leading-tight tracking-tight mb-0.5">
          {selectedAirportLabel || "Select"} Airport Parking
        </h1>
        <div className="flex flex-wrap items-center text-[#00261E] font-bold text-[13px] md:text-[14px]">
          <span className="mr-2 leading-relaxed">
            from: {moment(departure).format("MMM DD, YYYY HH:mm").toLowerCase()},
            to {moment(arrival).format("MMM DD, YYYY HH:mm").toLowerCase()}
          </span>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="flex items-center text-[#00261E] hover:opacity-70 transition-opacity mt-1 sm:mt-0 whitespace-nowrap"
          >
            <FaEdit className="mr-1 text-[16px]" /> Edit
          </button>
        </div>
      </div>

      <div className="w-full border-b border-dotted border-slate-700 "></div>

      {/* Animated Form Area */}
      <div
        className={`w-full overflow-hidden transition-all duration-[600ms] ease-in-out ${isEditing ? 'max-h-[800px] opacity-100 my-5' : 'max-h-0 opacity-0 mb-0'
          }`}
      >
        <div className="grid grid-cols-2 gap-x-3 md:gap-x-6 gap-y-5 w-full">
          {/* Airport Row */}
          <div className="col-span-2 flex flex-col w-full">
            <label className={labelClass}>Airport</label>
            <div className="relative w-full bg-[#E4EAE6] rounded-[8px]">
              <select
                value={airport}
                onChange={(e) => {
                  setAirport(e.target.value);
                  setValidation("");
                }}
                className={`${inputClass} appearance-none cursor-pointer ${validation ? 'border-2 border-red-500' : ''}`}
              >
                <option value="" disabled>Select airport</option>
                {airports?.map((a, idx) => (
                  <option key={idx} value={a.code}>{a.name}</option>
                ))}
              </select>
              <img
                src={require("../../common/images/select-airport.png")}
                alt="airport"
                className="absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] object-contain opacity-70 pointer-events-none"
              />
            </div>
            {validation && <span className="text-red-500 text-xs mt-1 font-bold">{validation}</span>}
          </div>

          {/* Pick-off Date & Time */}
          <div className="flex flex-col w-full">
            <label className={labelClass}>Pick-of Date</label>
            <div className="relative w-full  bg-[#E4EAE6] rounded-[8px]">
              <DatePicker
                portalId="root-portal"
                wrapperClassName="w-full"
                dateFormat="MMM d, yyyy"
                selected={moment(departure).toDate()}
                onChange={(date) => date && setDepartureDate(date)}
                minDate={moment().toDate()}
                placeholderText="date"
                className={inputClass}
              />
              <img src={require("../../common/images/date.webp")} alt="date" className="absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] object-contain opacity-70 pointer-events-none" />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <label className={labelClass}>Time</label>
            <div className="relative w-full bg-[#E4EAE6] rounded-[8px]">
              <select
                value={moment(departure).format("HH:mm")}
                onChange={(e) => setTime(e.target.value, "departure")}
                className={`${inputClass} appearance-none cursor-pointer pr-10`}
              >
                {times.map((o, index) => (
                  <option key={index} value={o}>{o}</option>
                ))}
              </select>
              <img src={require("../../common/images/time.webp")} alt="time" className="absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] object-contain opacity-70 pointer-events-none" />
            </div>
          </div>

          {/* Drop-off Date & Time */}
          <div className="flex flex-col w-full">
            <label className={labelClass}>Drop-of Date</label>
            <div className="relative w-full bg-[#E4EAE6] rounded-[8px]">
              <DatePicker
                portalId="root-portal"
                wrapperClassName="w-full"
                dateFormat="MMM d, yyyy"
                selected={moment(arrival).toDate()}
                onChange={(date) => date && setArrivalDate(date)}
                minDate={moment(departure).toDate()}
                placeholderText="date"
                className={inputClass}
              />
              <img src={require("../../common/images/date.webp")} alt="date" className="absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] object-contain opacity-70 pointer-events-none" />
            </div>
          </div>
          <div className="flex flex-col w-full">
            <label className={labelClass}>Time</label>
            <div className="relative w-full">
              <select
                value={moment(arrival).format("HH:mm")}
                onChange={(e) => setTime(e.target.value, "arrival")}
                className={`${inputClass} appearance-none cursor-pointer pr-10`}
              >
                {times.map((o, index) => (
                  <option key={index} value={o}>{o}</option>
                ))}
              </select>
              <img src={require("../../common/images/time.webp")} alt="time" className="absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] object-contain opacity-70 pointer-events-none" />
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Row: Promo Code + Get a Quote + Filter Bar */}
      <div className={`flex flex-col md:flex-row w-full relative mt-1 ${isEditing ? 'gap-6' : 'gap-0'}`}>

        {/* Promo + Quote Wrapper */}
        <div
          className={`flex gap-3 overflow-hidden transition-all duration-[600ms] ease-in-out ${isEditing
            ? "w-full md:w-[calc(50%-12px)] opacity-100 max-h-[500px]"
            : "w-0 max-h-0 opacity-0 m-0 border-none"
            }`}
        >
          <div className="flex-[3] flex flex-col min-w-[130px]">
            <label className={labelClass}>Promo Code</label>
            <div className="relative w-full">
              <input
                type="text"
                value={promocode}
                onChange={(e) => setPromoCode(e.target.value)}
                placeholder="promo code"
                className={`${inputClass} pr-10`}
              />
              <img src={require("../../common/images/promo-code.webp")} alt="promo" className="absolute right-4 top-1/2 -translate-y-1/2 w-[22px] h-[22px] object-contain opacity-70 pointer-events-none" />
            </div>
          </div>
          <div className="flex-[2] flex flex-col justify-end min-w-[120px]">
            <button
              onClick={handleSubmit}
              className="bg-[#00261E] text-white font-bold h-[48px] px-6 rounded-[8px] hover:bg-black transition-colors whitespace-nowrap flex items-center justify-center text-[14px]"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white rounded-full animate-spin border-t-transparent"></div>
              ) : (
                "Get a Quote"
              )}
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className={`flex flex-col justify-end transition-all duration-[600ms] ease-in-out ${isEditing ? "w-full md:w-[calc(50%-12px)]" : "w-full"
          }`}>
          <div className="flex flex-wrap items-center bg-[#F9FFBB] rounded-[8px] min-h-[48px] p-2 md:px-4 justify-between w-full shadow-sm gap-2">
            <div className="flex items-center gap-1 md:gap-3 flex-wrap">
              <button onClick={() => onFilterClick('all')} className={getFilterBtnClass('all')}>All</button>
              <button onClick={() => onFilterClick('Meet And Greet')} className={getFilterBtnClass('Meet And Greet')}>Meet and Greet</button>
              <button onClick={() => onFilterClick('Park And Ride')} className={getFilterBtnClass('Park And Ride')}>Park and Ride</button>
            </div>

            <div className="relative" ref={sortRef}>
              <button
                onClick={() => setIsSortOpen(!isSortOpen)}
                className="flex items-center gap-1 bg-transparent text-[#00261E] font-bold text-[11px] md:text-[12px] hover:opacity-70 transition-opacity whitespace-nowrap"
              >
                {getSortLabel()}
                <FaChevronDown className={`transition-transform duration-300 ml-1 text-[10px] ${isSortOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Custom Sort Dropdown */}
              {isSortOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-2xl overflow-hidden border border-gray-100 z-[100] animate-fade-in-down origin-top">
                  <div className="flex flex-col py-2">
                    <button
                      onClick={() => onSortClick('0')}
                      className={`px-4 py-2.5 text-left text-[13px] font-bold hover:bg-[#EEFE3E] hover:text-[#00261E] transition-colors ${activeSort === '0' ? 'bg-[#EEFE3E]/50 text-[#00261E]' : 'text-gray-700'
                        }`}
                    >
                      Price low to high
                    </button>
                    <button
                      onClick={() => onSortClick('1')}
                      className={`px-4 py-2.5 text-left text-[13px] font-bold hover:bg-[#EEFE3E] hover:text-[#00261E] transition-colors ${activeSort === '1' ? 'bg-[#EEFE3E]/50 text-[#00261E]' : 'text-gray-700'
                        }`}
                    >
                      Price high to low
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
